import React, { Ref, forwardRef } from 'react';
import classnames from 'classnames';
import { v4 as uuid } from 'uuid';
import { ErrorLabel } from '@eika/label';
import SelectLabel from './SelectLabel';
import '@eika/select-style';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
    /**
     * Label of select
     */
    label?: React.ReactNode;
    /**
     * Extra information below label
     */
    sublabel?: React.ReactNode | string;
    /**
     * Extra information button
     */
    infoButton?: React.ReactNode;
    /**
     * Values
     */
    values?: { text: string; value: string; disabled?: boolean; key?: string }[];
    /**
     * Validation error
     */
    error?: React.ReactNode | string;
}

const Select = forwardRef(
    (
        { label, sublabel, infoButton, values, error, children, className, id, ...rest }: SelectProps,
        ref: Ref<HTMLSelectElement>
    ): JSX.Element => {
        const inputId = id || uuid();
        const errorMessage = error;
        const errorMessageId = `${inputId}-error`;

        return (
            <div>
                <SelectLabel label={label} sublabel={sublabel} infoButton={infoButton} />
                <div className={classnames('sgw-select', className)}>
                    <select
                        id={inputId}
                        ref={ref}
                        className={classnames({ 'sgw-select__error': !!error })}
                        aria-invalid={!!errorMessage}
                        aria-describedby={typeof sublabel === 'string' ? sublabel : ''}
                        aria-errormessage={errorMessage ? errorMessageId : undefined}
                        {...rest}
                    >
                        {values && values?.length > 0
                            ? values.map((option) => (
                                  <option
                                      value={option.value}
                                      key={option.key || option.value}
                                      disabled={option.disabled || false}
                                  >
                                      {option.text}
                                  </option>
                              ))
                            : children}
                    </select>
                </div>
                {error && typeof error === 'string' && <ErrorLabel className="sgw-select__error-label" text={error} />}
            </div>
        );
    }
);

export default Select;
